'use client';

import { HeroButton } from '@app/app/_components/HeroButton/HeroButton';
import { Modal } from '@app/app/_components/Modal/Modal';
import { useFontSize } from '@app/app/_context/AccessibilityContext/useFontSize';
import { KeyIcon } from '@app/app/_icons/KeyIcon';
import { PrivatePersonIcon } from '@app/app/_icons/PrivatePersonIcon';
import { LoginWidget } from '@app/app/_widgets/Login/LoginWidget';
import { SelectClientWidget } from '@app/app/_widgets/SelectClient/SelectClientWidget';
import { Client } from '@app/domain/model/Client';
import { FontSize } from '@app/domain/model/FontSize';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { useEffectOnce } from '@app/lib/use-change-effect/useEffectOnce';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

interface Props {
    authState: AuthState;
    clients: Client[];
    onContinue?: () => void;
    onSuccess?: () => void;
    swedbankLogoSrc: string;
    sebLogoSrc?: string;
    noGuestOption?: boolean;
    bankAuthEnabled?: boolean;
}

export function CustomerLoginStep({
    authState,
    clients,
    onContinue,
    onSuccess,
    swedbankLogoSrc,
    sebLogoSrc,
    noGuestOption = false,
    bankAuthEnabled = false,
}: Props) {
    const t = useTranslations();
    const fontSize = useFontSize();

    const [loginOpen, setLoginOpen] = useState(false);
    const [selectClientOpen, setSelectClientOpen] = useState(false);

    const handleLoginClick = useCallback(() => {
        setLoginOpen(true);
    }, []);

    const handleLoginClose = useCallback(() => {
        setLoginOpen(false);
    }, []);

    const handleSelectClientClose = useCallback(() => {
        setSelectClientOpen(false);
    }, []);

    const handleSuccess = useCallback(() => {
        onSuccess?.();
    }, [onSuccess]);

    const handleLoginSuccess = useCallback(() => {
        handleLoginClose();

        if (authState.clientSelected) {
            return handleSuccess();
        }
    }, [handleLoginClose, authState.clientSelected, handleSuccess]);

    const handleSelectClientSuccess = useCallback(() => {
        handleSelectClientClose();

        handleSuccess();
    }, [handleSelectClientClose, handleSuccess]);

    const handleSelectClientBack = useCallback(async () => {
        handleSelectClientClose();
        setLoginOpen(true);
    }, [handleSelectClientClose]);

    useChangeEffect(() => {
        if (clients.length > 0 && !authState.clientSelected) {
            setSelectClientOpen(true);
        }
    }, clients.length);

    useEffectOnce(() => {
        if (authState.authenticated && authState.clientSelected) {
            onSuccess?.();
            return;
        }

        if (clients.length > 0 && !authState.clientSelected) {
            setSelectClientOpen(true);
        }
    });

    return (
        <>
            <div className="flex flex-col gap-4">
                <HeroButton
                    icon={<KeyIcon className={twMerge('size-14', fontSize === FontSize.Huge && 'max-md:hidden')} />}
                    onClick={handleLoginClick}>
                    <div className="flex flex-col gap-1 text-left">
                        <div className="text-lg font-medium">{t('eshop.general.compensaCustomerLogin')}</div>
                        <div className="text-neutral-500">{t('eshop.general.compensaCustomerLoginDescription')}</div>
                    </div>
                </HeroButton>

                {!noGuestOption && (
                    <HeroButton
                        icon={
                            <PrivatePersonIcon
                                className={twMerge('size-14', fontSize === FontSize.Huge && 'max-md:hidden')}
                            />
                        }
                        onClick={onContinue}>
                        <div className="flex flex-col gap-1 text-left">
                            <div className="text-lg font-medium">{t('eshop.general.continueAsGuest')}</div>
                            <div className="text-neutral-500">{t('eshop.general.continueAsGuestDescription')}</div>
                        </div>
                    </HeroButton>
                )}
            </div>

            <Modal cardClassName="gap-8 p-8 md:min-w-dialog" open={loginOpen} onClose={handleLoginClose}>
                <LoginWidget
                    disableTwoFactorAuth
                    noBankAuth={!bankAuthEnabled}
                    swedbankLogoSrc={swedbankLogoSrc}
                    sebLogoSrc={sebLogoSrc}
                    onSuccess={handleLoginSuccess}
                />
            </Modal>

            <Modal
                cardClassName="gap-8 p-8 md:min-w-dialog"
                open={selectClientOpen}
                onClose={handleSelectClientClose}
                canClose={false}>
                <SelectClientWidget
                    clients={clients}
                    canGoBack={true}
                    onSuccess={handleSelectClientSuccess}
                    onBack={handleSelectClientBack}
                />
            </Modal>
        </>
    );
}
